import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { ControllerFontType } from '@bymason-private/controller-v1-theme-provider';
import {
  ArtifactType,
  useGetArtifactListByVersionList,
  ArtifactModel,
  ConfigModel,
  GetArtifactsOptionsList,
} from '@bymason/controller-api-hooks';
import { Text } from '@bymason-private/legacy-pattern-components';
import { useToast } from '@bymason-private/controller-shared-widgets';
import ArtifactsList from '../../views/artifacts/artifacts-list';
import { YamlConfigDisplay } from '../yaml-display';

export const ConfigReview = ({ projectName, config, onInvalidArtifacts, otherAlert }: ConfigReviewProps) => {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const artifactsToValidate = useRef<GetArtifactsOptionsList>([]);
  const [
    getArtifacts,
    artifactsList,
    artifactsListError,
    artifactsListBusy,
  ] = useGetArtifactListByVersionList(authToken);

  const artifactsMap = useMemo(() => {
    return (
      artifactsList?.reduce<Record<string, ArtifactModel[]>>((map, art) => {
        if (art) {
          const key =
            art.type === ArtifactType.Apk ? 'apks' : art.type === ArtifactType.Media && art.media.type;
          if (key) {
            map[key] = map[key] ? [...map[key], art] : [art];
          }
        }
        return map;
      }, {}) || {}
    );
  }, [artifactsList]);

  useEffect(() => {
    if (customerId && config) {
      const optionsList = (config?.apps || []).map((app) => {
        const { package_name: name, version_code: version } = app;
        return { customerId, type: ArtifactType.Apk, name, version };
      });
      if (config.media?.splash) {
        const { name, version } = config.media.splash;
        optionsList.push({ customerId, type: ArtifactType.Media, name, version });
      }
      if (config.media?.bootanimation) {
        const { name, version } = config.media.bootanimation;
        optionsList.push({ customerId, type: ArtifactType.Media, name, version });
      }

      artifactsToValidate.current = optionsList;
      getArtifacts(optionsList);
    }
  }, [customerId, getArtifacts, config]);

  useEffect(() => {
    const errorMessage = artifactsListError || '';
    if (errorMessage && setToastMessage && setToastSeverity) {
      setToastMessage(errorMessage);
      setToastSeverity('error');
    }
  }, [artifactsListError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    const invalid = ((artifactsList || [])
      .map((art, idx) => (art === undefined ? artifactsToValidate.current[idx] : null))
      .filter((art) => art !== null) as unknown) as GetArtifactsOptionsList;
    onInvalidArtifacts && onInvalidArtifacts(invalid);
  }, [artifactsList, onInvalidArtifacts]);

  return !artifactsListBusy ? (
    <div>
      {otherAlert && otherAlert}
      {artifactsMap && (
        <div>
          {artifactsMap['apks'] && (
            <>
              <Text sx={{ lineHeightPx: 24 }} variant={ControllerFontType.Text500M} component='div'>
                Apps
              </Text>
              <ArtifactsList artifacts={artifactsMap['apks']} />
            </>
          )}
          {artifactsMap['splash'] && (
            <>
              <Text sx={{ lineHeightPx: 24 }} variant={ControllerFontType.Text500M} component='div'>
                Splash image
              </Text>
              <ArtifactsList artifacts={artifactsMap['splash']} />
            </>
          )}
          {artifactsMap['bootanimation'] && (
            <>
              <Text sx={{ lineHeightPx: 24 }} variant={ControllerFontType.Text500M} component='div'>
                Boot animation
              </Text>
              <ArtifactsList artifacts={artifactsMap['bootanimation']} />
            </>
          )}
        </div>
      )}
      {config && (
        <>
          <Text sx={{ lineHeightPx: 24 }} variant={ControllerFontType.Text500M} component='div'>
            Config
          </Text>
          <YamlConfigDisplay title={`Mason OS Config for project ${projectName}`} configJson={config} />
        </>
      )}
    </div>
  ) : null;
};

export interface ConfigReviewProps {
  projectName: string;
  config: ConfigModel;
  onInvalidArtifacts?: (invalidArtifacts: GetArtifactsOptionsList) => void;
  otherAlert?: JSX.Element | null;
}
